import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";
import { exportBillDetailApi } from "../services/reports.service";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "Bill Date ",
    value: "billDate",
  },
  {
    id: 3,
    label: "Patient UHID",
    value: "patientUhid",
  },
  {
    id: 4,
    label: "Bill Number",
    value: "billNumber",
  },
  {
    id: 5,
    label: "Visit No",
    value: "visitNo",
  },
  {
    id: 6,
    label: "Status",
    value: "status",
  },
  {
    id: 7,
    label: "Patient Name",
    value: "patientName",
  },
];

// Proper dropdown options for Bill Detail Report
const templateOptions = [
  { label: "All Bills", value: "all_bills" },
  { label: "OPD Bills", value: "opd_bills" },
  { label: "IPD Bills", value: "ipd_bills" },
  { label: "Emergency Bills", value: "emergency_bills" },
  { label: "Pharmacy Bills", value: "pharmacy_bills" },
  { label: "Lab Bills", value: "lab_bills" },
  { label: "Radiology Bills", value: "radiology_bills" },
];

const patientUHIDOptions = [
  { label: "KSG-24-0000032", value: "KSG-24-0000032" },
  { label: "KSG-24-0000033", value: "KSG-24-0000033" },
  { label: "KSG-24-0000034", value: "KSG-24-0000034" },
  { label: "KSG-24-0000035", value: "KSG-24-0000035" },
  { label: "KSG-24-0000036", value: "KSG-24-0000036" },
];

const billNoOptions = [
  { label: "BILL1001", value: "BILL1001" },
  { label: "BILL1002", value: "BILL1002" },
  { label: "BILL1003", value: "BILL1003" },
  { label: "BILL1004", value: "BILL1004" },
  { label: "BILL1005", value: "BILL1005" },
];

const visitNoOptions = [
  { label: "VST2001", value: "VST2001" },
  { label: "VST2002", value: "VST2002" },
  { label: "VST2003", value: "VST2003" },
  { label: "VST2004", value: "VST2004" },
  { label: "VST2005", value: "VST2005" },
];

const statusOptions = [
  { label: "Paid", value: "paid" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Partial", value: "partial" },
  { label: "Refunded", value: "refunded" },
];

export default function ReportsBillDetail() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    requestedDate: null,
    patientUhid: null,
    billNo: null,
    visitNo: null,
    status: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    billDate: true,
    patientUhid: true,
    billNumber: true,
    visitNo: true,
    status: true,
    patientName: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Bill Detail Report",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.info("Link copied to clipboard");
    }
  };

  const handleMenuClick = (item) => {
    handleClose();
    if (item === "Excel") {
      handleExport();
    } else if (item === "PDF") {
      toast.info("PDF export feature coming soon");
    } else {
      toast.info(`${item} feature coming soon`);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.requestedDate) {
      errorFields.push('requestedDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    setIsLoading(true);
    
    try {
      // Simulate API call - replace with actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock data for demonstration - replace with actual API response
      const mockData = [
        {
          _id: "1",
          sno: "1",
          billDate: "2025-02-25",
          patientUhid: "KSG-24-0000032",
          billNumber: "BILL1001",
          visitNo: "VST2001",
          status: "Paid",
          patientName: "John Doe"
        },
        {
          _id: "2",
          sno: "2",
          billDate: "2025-02-24",
          patientUhid: "KSG-24-0000033",
          billNumber: "BILL1002",
          visitNo: "VST2002",
          status: "Pending",
          patientName: "Jane Smith"
        },
        {
          _id: "3",
          sno: "3",
          billDate: "2025-02-23",
          patientUhid: "KSG-24-0000034",
          billNumber: "BILL1003",
          visitNo: "VST2003",
          status: "Cancelled",
          patientName: "Alice Johnson"
        },
        {
          _id: "4",
          sno: "4",
          billDate: "2025-02-22",
          patientUhid: "KSG-24-0000035",
          billNumber: "BILL1004",
          visitNo: "VST2004",
          status: "Paid",
          patientName: "Michael Brown"
        },
        {
          _id: "5",
          sno: "5",
          billDate: "2025-02-21",
          patientUhid: "KSG-24-0000036",
          billNumber: "BILL1005",
          visitNo: "VST2005",
          status: "Pending",
          patientName: "Emma Wilson"
        }
      ];
      
      setTableData(mockData);
      toast.success("Bill detail data loaded successfully");
    } catch (error) {
      console.error("Error fetching bill detail data:", error);
      toast.error("Failed to load bill detail data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (tableData.length === 0) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Prepare export parameters
      const exportParams = {
        searchTemplate: formData.searchTemplate?.value,
        requestedDate: formData.requestedDate?.toISOString(),
        patientUhid: formData.patientUhid?.value,
        billNumber: formData.billNo?.value,
        page: 1,
        limit: 1000 // Export all records
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      const result = await exportBillDetailApi(exportParams);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Bill Detail Report
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="requestedDate"
                  label="Requested Date"
                  value={formData.requestedDate}
                  onChange={(newValue) => {
                    handleInputChange('requestedDate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('requestedDate')) {
                      setErrors(errors.filter(e => e !== 'requestedDate'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('requestedDate') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={patientUHIDOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.patientUhid}
                  onChange={(event, newValue) => handleInputChange('patientUhid', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patient UHID"
                      name="patientUhid"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={billNoOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.billNo}
                  onChange={(event, newValue) => handleInputChange('billNo', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bill No"
                      name="billNo"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={visitNoOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.visitNo}
                  onChange={(event, newValue) => handleInputChange('visitNo', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visit No"
                      name="visitNo"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={statusOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.status}
                  onChange={(event, newValue) => handleInputChange('status', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      name="status"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || tableData.length === 0}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
              {/* dynamic button */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {tableData?.length || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleClick}>
                      <IconButton>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="bill detail report table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {visibleColumns.sno && <TableCell>SNO</TableCell>}
                      {visibleColumns.billDate && <TableCell>Bill Date</TableCell>}
                      {visibleColumns.patientUhid && <TableCell>Patient UHID</TableCell>}
                      {visibleColumns.billNumber && <TableCell>Bill Number</TableCell>}
                      {visibleColumns.visitNo && <TableCell>Visit No</TableCell>}
                      {visibleColumns.status && <TableCell>Status</TableCell>}
                      {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((row) => (
                        <TableRow key={row?._id}>
                          {visibleColumns.sno && <TableCell>{row?.sno}</TableCell>}
                          {visibleColumns.billDate && <TableCell>{row?.billDate}</TableCell>}
                          {visibleColumns.patientUhid && <TableCell>{row?.patientUhid}</TableCell>}
                          {visibleColumns.billNumber && <TableCell>{row?.billNumber}</TableCell>}
                          {visibleColumns.visitNo && <TableCell>{row?.visitNo}</TableCell>}
                          {visibleColumns.status && <TableCell>{row?.status}</TableCell>}
                          {visibleColumns.patientName && <TableCell>{row?.patientName}</TableCell>}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
          </Box>

              {/* Download Menu */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>

              {/* Filter Menu for Column Visibility */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => toggleColumn('sno')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>SNO</Box>
                    {visibleColumns.sno && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('billDate')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Bill Date</Box>
                    {visibleColumns.billDate && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientUhid')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient UHID</Box>
                    {visibleColumns.patientUhid && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('billNumber')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Bill Number</Box>
                    {visibleColumns.billNumber && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('visitNo')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Visit No</Box>
                    {visibleColumns.visitNo && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('status')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Status</Box>
                    {visibleColumns.status && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Name</Box>
                    {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              </Menu>
        </Box>
      </Box>
    </Container>
  );
}
